import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { timeFormat } from 'd3';
import { format } from 'd3-format';
import { timeParse } from 'd3-time-format';
import { Observable, Subscription } from 'rxjs';

import { GoalStatus } from '../../coaching-base/general';
import { decideColor } from '../../coaching-detail/pillar-three/chart';
import {
    BaseLineGraphData,
    EventTooltipData,
    GoalTooltipData,
    GraphDefinition,
    GraphPoint,
    LineAreaOptions,
} from '../../coaching-insight/line-area.component';
import {
    addDays,
    CoachingDateToday,
    getMonthStart,
} from '../../coaching-insight/oa/models/coaching.global.model';
import {
    GraphLabel,
    GraphType,
    Metric,
    Opportunity,
    RawGraphData,
} from '../../coaching-insight/oa/models/oa.model';
import { CmpsDatePipe } from '../../common/date.pipe';
import { CmpsDecimalPipe } from '../../common/number.pipe';
import {
    MetricDataItem,
    percentToTargetRanked,
} from '../../insight/performance-data/metric.pd.model';
import { PeriodType } from '../../widget/date-range.widget.model';
import { TranslationKeyMap } from '../coaching-commitment.i18n';

const colors = [
    {
        targetColour: '#137E0F',
        targetColour2: '#137E0F',
        supergroupColour: '#ce221e',
        performerColour: '#1E60D4',
        performerColour2: 'palevioletred',
        goalColour: '#D17300',
        goalColour2: '#363636',
    },
];

const parsedtg = timeParse('%Y-%m-%d');
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const clamp = (a: number) =>
    typeof a === 'number' ? Math.min(Math.max(a, -100), 100) : 0;

@Component({
    selector: 'goal-chart-commitment',
    template: `
        <div
            class="sg-toggle"
            *ngIf="graphType !== GraphType.VARIANCE"
        >
            <label>{{ i18n._9429_SHOW_SUPERGROUP_AVG | translate }}</label>
            <input
                type="checkbox"
                [checked]="graphSetSGToggles[0]"
                (change)="updateDisplaySGAvg()"
            />
        </div>
        <div
            class="no-data-message"
            *ngIf="
                showEmptyGraph &&
                !loadingGraph &&
                activeMetric?.metric?.metricId
            "
        >
            {{ i18n._9430_NO_METRIC_PERFORMANCE_DATA | translate }}
        </div>
        <ng-chart-line-area
            *ngIf="!allPropsEmpty(data) && !graphSetDataEmpty()"
            [graphSet]="graphSet"
            [graphSetSGToggles]="
                graphType === GraphType.VARIANCE ? false : graphSetSGToggles
            "
            [isVariance]="graphType === GraphType.VARIANCE ? true : false"
            [customHeight]="graphHeight"
            [maxTooltipHeight]="maxTooltipHeight"
            [maxTooltipWidth]="maxTooltipWidth"
        >
            <ng-template
                lineAreaChartEventCallout
                let-data="data"
            >
                <cmps-coaching-event-box
                    [events]="data"
                ></cmps-coaching-event-box>
            </ng-template>
            <ng-template
                lineAreaChartGoalCallout
                let-data="data"
            >
                <cmps-coaching-goal-box
                    [goalLabel]="i18n._721_GOAL | translate"
                    [goal]="
                        data?.goalValue == null
                            ? '-'
                            : (data?.goalValue | number: '1.0-2')
                    "
                    [pctToGoalLabel]="i18n._767___TO_GOAL | translate"
                    [pctToGoal]="
                        data?.currentPctGoal == null
                            ? '-'
                            : (data?.currentPctGoal | percent: '1.0-2')
                    "
                    [pctToTargetLabel]="i18n._768___TO_TARGET | translate"
                    [pctToTarget]="
                        data?.percentToTarget == null
                            ? '-'
                            : (data?.percentToTarget | percent: '1.0-2')
                    "
                ></cmps-coaching-goal-box>
            </ng-template>
        </ng-chart-line-area>
    `,
    // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GoalChartCommitmentComponent implements OnChanges, OnDestroy {
    @Input() data: RawGraphData[];
    @Input() activeMetric: Opportunity;
    @Input() graphType: GraphType = GraphType.VARIANCE;
    @Input() periodType: PeriodType;
    @Input() componentData: RawGraphData[] = null;
    @Input() goalStatusId: GoalStatus;
    @Input() showEmptyGraph: boolean = true;
    @Output() doneRendering: EventEmitter<boolean> = new EventEmitter<boolean>(
        null,
    );

    lineareaData: BaseLineGraphData;
    optionsLineArea: LineAreaOptions;

    _resizeObs: Observable<any>;
    resizesub: Subscription;

    selectedComponents = [];
    graphLabels: GraphLabel[] = [];

    GraphType = GraphType;

    varianceColour: string;
    targetColour: string = '#7a7a7a';
    supergroupColour: string = '#ce221e';
    performerColour: string = '#7a7a7a';
    goalColour: string = '#4ba337';
    periodStartColour: string = '#345ea3';
    opportunityColour: string = '#FF0000';

    hasGoals: boolean = false;

    PeriodType = PeriodType;

    periodstartdate: Date;

    labelSets: any[] = [];
    graphSet: GraphDefinition[] = [];
    // graphSetSGToggles: Array<boolean> = [];
    graphSetSGToggles: boolean[] = [true];

    colourcounter: number = 0;

    i18n = TranslationKeyMap;

    loadingGraph: boolean = false;

    graphHeight: number = 250;

    maxTooltipHeight = 150;
    maxTooltipWidth = 180;

    constructor(
        private readonly _decimalPipe: CmpsDecimalPipe,
        private readonly _datePipe: CmpsDatePipe,
        private readonly _translateService: TranslateService,
    ) {}

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnChanges(a) {
        // eslint-disable-next-line no-prototype-builtins
        if (a.hasOwnProperty('data') && this.data && this.data.length) {
            this.reset();
            this.graphSet = [];
            this.graphSetSGToggles = [true];
            this.colourcounter = 0;
            this.data.forEach((d) => {
                this.initGraphConfig(d);
            });
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    initGraphConfig(data?: RawGraphData) {
        if (this.periodType) {
            this.periodstartdate =
                this.periodType === PeriodType.ROLLING_30
                    ? addDays(CoachingDateToday, -30)
                    : getMonthStart(CoachingDateToday);
        }

        let metric = this.activeMetric.metric;
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (data && data.component && data.component.metric) {
            // Component Opportunity
            metric = data.component.metric;
        }

        let opportunity = 0;
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (this.activeMetric && this.activeMetric.metric.metricId) {
            opportunity = 0.01; // oaThresholdValue

            if (opportunity > 0) {
                opportunity *= -1;
            }
        }
        this.loadingGraph = true;

        const finalgraphdata = this.initLineData(data);
        const finalgraphoptions = this.initLineOptions(
            opportunity,
            metric,
            finalgraphdata.goalData != null,
            true,
        );

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const graphdef = {
            data: finalgraphdata,
        } as GraphDefinition;

        this.graphSet = [...this.graphSet, graphdef];

        if (!this.graphSetDataEmpty()) {
            graphdef.options = finalgraphoptions;
        } else {
            this.labelSets.pop();
        }

        this.graphSetSGToggles = [true]; // default SG visibility to false

        this.loadingGraph = false;

        this.doneRendering.emit(false);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    appendGraphConfig(d: RawGraphData) {
        this.initGraphConfig(d);
    }

    initLineData(data?: RawGraphData): BaseLineGraphData {
        let linedata: BaseLineGraphData = null;

        if (data) {
            const dNew: BaseLineGraphData = {};

            if (data.mainData) {
                dNew.mainData = this.processMainData(data);
                dNew.eventData = this.processEventData(dNew.mainData, data);
            }

            const tg = this.processTargetAndGoalData(data);
            dNew.targetData = tg.targetData;
            dNew.goalData = tg.goalData;

            if (this.graphType !== GraphType.VARIANCE) {
                dNew.supergroupData = this.processSuperGroupData(data);
            }
            linedata = dNew;
            return linedata;
        }
        return linedata;
    }

    processEventData(mainData: GraphPoint[], data: RawGraphData): GraphPoint[] {
        // base events to the actual metric performance
        let eventData = [];

        if (data.eventData) {
            data.eventData.forEach((a) => {
                const earlierOrEqual = mainData.filter(
                    (b) => b.x.getTime() <= a.date.getTime(),
                );
                const tooltipdata: EventTooltipData[] = [
                    {
                        eventId: a.eventId,
                        commitmentStatusName: a.commitmentStatusName,
                        date: a.date,
                        eventTypeName: a.eventTypeName,
                        performer: a.performer,
                        statusColour: decideColor(a.commitmentStatusId),
                        eventTypeId: a.eventTypeId,
                        startTime: a.startTime,
                        endTime: a.endTime,
                    },
                ];

                if (earlierOrEqual.length) {
                    eventData.push({
                        x: a.date,
                        y: earlierOrEqual[earlierOrEqual.length - 1].y, // get closest lower date, and get its y value
                        tooltipData: tooltipdata,
                    });
                } else {
                    if (mainData.length) {
                        // if nothing is earlier than this date, then this is the earliest
                        eventData.unshift({
                            x: a.date,
                            y: mainData[0].y, // get lowest date, and get its y value
                            tooltipData: tooltipdata,
                        });
                    }
                }
            });

            // already sorted from earliest to latest date (x)
            eventData = this._getUniqueEvents(eventData);
        }

        return eventData;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    _getUniqueEvents(evtdata: GraphPoint[]) {
        let eventData = [...evtdata];

        if (eventData.length) {
            // get multiples
            const uniquedates = [];
            eventData.forEach((a) => {
                const found = uniquedates.find((b) => {
                    b.x.setHours(0, 0, 0, 0);
                    a.x.setHours(0, 0, 0, 0);
                    return b.x.getTime() === a.x.getTime();
                });
                if (!found) {
                    uniquedates.push(a);
                } else {
                    found.tooltipData.push(...a.tooltipData);
                }
            });

            eventData = uniquedates;
        }
        return eventData;
    }

    processTargetAndGoalData(data: RawGraphData): {
        targetData: GraphPoint[];
        goalData: GraphPoint[];
    } {
        let targetData = [];
        let goalData = [];

        // use varianceData, take target property and goal property
        if (this.graphType === GraphType.TREND) {
            // get all target data from the existing variance data (only ones with values and dates)
            targetData = this._processTarget(data);
            goalData = this._processGoal(data);

            if (goalData.length) {
                this.hasGoals = true;
            }
        }

        return {
            targetData,
            goalData,
        };
    }

    _processTarget(data: RawGraphData): GraphPoint[] {
        const td = data.mainData.filter((v) => v.target != null);
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        if (td && td.length) {
            const targetData = td.map((d: any): GraphPoint => {
                const evtdate =
                    typeof d.eventDate === 'string'
                        ? parsedtg(d.eventDate.split('T')[0])
                        : d.eventDate;
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                return {
                    x: evtdate,
                    y: d.target,
                } as GraphPoint;
            });

            return targetData.sort((a: GraphPoint, b: GraphPoint) => {
                return a.x - b.x;
            });
        }

        return [];
    }

    _processGoal(data: RawGraphData): GraphPoint[] {
        // get all goal data from the existing variance data (only ones with values and dates)
        const gd = data.goalData;
        gd.sort(
            (x, y) =>
                new Date(x.date).getTime() - new Date(y.date).getTime() ||
                x.goalId - y.goalId,
        );

        let last: MetricDataItem;
        if (data.mainData.length) {
            // expected to be already sorted
            last = [...data.mainData].reverse()[0];
        }

        return (gd || []).map((d: any, idx: number): GraphPoint => {
            let tooltipData: GoalTooltipData;
            if (last) {
                tooltipData = {
                    goalValue: d.goalValue,
                    // TODO this calculation should be consolidated
                    currentPctGoal:
                        d.goalValue === 0 || d.goalValue == null
                            ? null
                            : ((last.actual - d.goalValue) / d.goalValue) *
                                  (last.rankOrder === 0 ? -1 : 1) +
                              1,
                    percentToTarget: percentToTargetRanked(
                        last.actual,
                        last.target,
                        last.rankOrder,
                    ),
                };
            }

            const date = d.expectedAchievementDate;
            const evtdate =
                typeof date === 'string' ? parsedtg(date.split('T')[0]) : date;
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            return {
                x: evtdate,
                y: d.goalValue,
                tooltipData,
            } as GraphPoint;
        });
    }

    processMainData(data: RawGraphData): GraphPoint[] {
        let mainData = [];
        mainData = data.mainData.map((d: any) => {
            const evtdate =
                typeof d.eventDate === 'string'
                    ? d.eventDate.split('T')[0]
                    : d.eventDate;
            const rank = d.rankOrder === 0 ? -1 : 1;
            let tgt = d.target != null ? d.target : d.actual;
            tgt = tgt === 0 ? 0.00001 : tgt;
            const variance =
                this.graphType === GraphType.VARIANCE
                    ? ((d.actual - tgt) / tgt) * rank * 100
                    : d.actual;

            return {
                x: typeof evtdate !== 'string' ? evtdate : parsedtg(evtdate),
                y:
                    this.graphType === GraphType.VARIANCE
                        ? clamp(variance)
                        : d.actual,
            };
        });

        return mainData.sort((a: GraphPoint, b: GraphPoint) => {
            return a.x - b.x;
        });
    }

    processSuperGroupData(data: RawGraphData): GraphPoint[] {
        let supergroupData = [];

        // separate call's data, supergroup
        if (data.sgAvgData.length) {
            // target will be the same
            supergroupData = data.sgAvgData.map((d: any): GraphPoint => {
                const evtdate =
                    typeof d.eventDate === 'string'
                        ? d.eventDate.split('T')[0]
                        : d.eventDate;
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                return {
                    x: parsedtg(evtdate),
                    y: d.actual,
                } as GraphPoint;
            });
            supergroupData.sort((a: GraphPoint, b: GraphPoint) => {
                return a.x - b.x;
            });
        }

        return supergroupData;
    }

    initLineOptions(
        opportunity?: number,
        metric?: Metric,
        hasGoals = false,
        shouldAddLabels = false,
    ): LineAreaOptions {
        let opts: LineAreaOptions;

        if (!metric) {
            return null;
        }

        opts = this._genOpts(metric);

        this.checkAddLabels(metric, shouldAddLabels, hasGoals);

        opts = this.addLabelsToOpts(opts, opportunity);

        return opts;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    addLabelsToOpts(opts: LineAreaOptions, opportunity: number) {
        const thresholds = { yAxis: null, xAxis: null };

        // red and green area
        const thresoldYAxis = {
            label: 'Target',
            value: 0,
            strokeColor: this.showEmptyGraph
                ? 'transparent'
                : opportunity
                ? this.targetColour
                : 'transparent',
            aboveColor: this.showEmptyGraph
                ? 'transparent'
                : 'rgba(83,165,77, 0.8)',
            belowColor: this.showEmptyGraph
                ? 'transparent'
                : 'rgba(255, 173, 64, 0.8)',
        };

        // blue waterfall area
        if (this.graphType === GraphType.TREND) {
            thresoldYAxis.aboveColor = this.showEmptyGraph
                ? 'transparent'
                : 'rgba(30, 96, 212, 0.3)';
            thresoldYAxis.belowColor = this.showEmptyGraph
                ? 'transparent'
                : 'rgba(30, 96, 212, 0.3)';
        }

        thresholds.yAxis = [
            {
                label: 'Opportunity',
                value: opportunity,
                strokeColor: opportunity
                    ? this.opportunityColour
                    : 'transparent',
                belowColor: this.showEmptyGraph
                    ? 'transparent'
                    : 'rgba(231, 65, 65, 0.8)',
            },
        ];

        thresholds.yAxis.unshift(thresoldYAxis);

        thresholds.xAxis = [
            {
                label: 'Curr. Period Start',
                value: this.periodstartdate,
                strokeColor: this.periodStartColour,
            },
        ];

        opts.thresholds = thresholds;

        opts.showTinyCircles = ![
            PeriodType.ROLLING_30,
            PeriodType.DAY,
        ].includes(this.periodType);

        opts.periodType = this.periodType;
        return opts;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    checkAddLabels(metric: Metric, shouldAddLabels = false, hasGoals = false) {
        if (shouldAddLabels) {
            this.labelSets.push({
                name: metric.metricShortName,
                performerColour: colors[this.colourcounter].performerColour,
                supergroupColour: colors[this.colourcounter].supergroupColour,
                targetColour: colors[this.colourcounter].targetColour,
                goalColour: colors[this.colourcounter].goalColour,
                periodStartColour: this.periodStartColour,
                opportunityColour: this.opportunityColour,
                showSG: false,
                hasGoals,
            });
        }
    }

    _genOpts(metric: Metric): LineAreaOptions {
        const opts = {
            yTickLabel(d) {
                return d;
            },
            xTickLabel: timeFormat('%b %d'),
            showLegend: false,
            dataLabel: (data: any) => {
                let formaty = '';
                if (data.y == null) {
                    return this._translateService.instant(
                        TranslationKeyMap._711_NO_DATA_AVAILABLE,
                    );
                }
                const val = this._decimalPipe.transform(data.y, '1.2-2');

                if (
                    this.graphType === GraphType.VARIANCE ||
                    // eslint-disable-next-line @typescript-eslint/prefer-includes
                    (metric.metricName && metric.metricName.indexOf('%') > -1)
                ) {
                    formaty = ' %';
                }

                return `${val}${formaty}`; // % variance
            },
            lineStrokeColor: this.showEmptyGraph
                ? 'transparent'
                : colors[this.colourcounter].performerColour,
            lineStrokeColor2: this.showEmptyGraph
                ? 'transparent'
                : colors[this.colourcounter].performerColour2,
            eventDotsColor: colors[this.colourcounter].performerColour,
            eventDotsColor2: colors[this.colourcounter].performerColour2,
            sgLineStrokeColor: colors[this.colourcounter].supergroupColour,
            tLineStrokeColor: this.showEmptyGraph
                ? 'transparent'
                : colors[this.colourcounter].targetColour,
            tLineStrokeColor2: this.showEmptyGraph
                ? 'transparent'
                : colors[this.colourcounter].targetColour2,
            gLineStrokeColor: colors[this.colourcounter].goalColour,
            gLineStrokeColor2: colors[this.colourcounter].goalColour2,
            showTinyCircles: false,
            showEventCircles: true,
            pendingGoal:
                this.goalStatusId === GoalStatus.PRE_APPROVAL ||
                this.goalStatusId === GoalStatus.UPDATING,
            showVerticalThresholdLines: false,
            // graphLabel: this.metricselected.Description
        };

        const percentageFormat = format('>.2%');

        if (this.graphType === GraphType.VARIANCE) {
            opts.yTickLabel = function (d) {
                if (!Number.isInteger(d)) {
                    // Convert to decimal format and format as percentage with 2 decimal places
                    d = percentageFormat(d / 100);
                } else {
                    // Otherwise, just convert integer to percentage string
                    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                    d = `${d}%`;
                }
                return d;
            };
        }
        return opts;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    reset() {
        this.lineareaData = null;
        this.optionsLineArea = null;
        this.labelSets = [];
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    allPropsEmpty(data: any) {
        for (const g in data) {
            if (data[g]) {
                return false;
            }
        }
        return true;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    graphSetDataEmpty() {
        let emptycount = 0;
        const gslen = this.graphSet.length;
        this.graphSet.forEach((gs) => {
            if (
                !gs ||
                !gs.data ||
                !gs.data.mainData ||
                !gs.data.mainData.length
            ) {
                emptycount++;
            }
        });

        if (emptycount === gslen) {
            return true;
        }

        return false;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    updateDisplaySGAvg() {
        const toggled = !this.graphSetSGToggles[0];
        this.graphSetSGToggles = [toggled];
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    ngOnDestroy() {
        this.reset();
    }
}
